import { useState } from 'react'
import { createPortal } from 'react-dom'
import type { ConversaDto, EtapaDto, NegociacaoDto, UsuarioSessao } from '@crm/shared'
import { useEquipe } from '../../api/hooks'
import {
  useCampos,
  useFunis,
  useHistoricoDoContato,
  useMotivosPerda,
  useMoverEtapa,
  useNovaNegociacao,
  useTags,
} from '../../api/hooks-leads'
import { usePerguntar } from '../../componentes/perguntar'
import { QuadradinhosDoFunil } from '../../componentes/QuadradinhosDoFunil'
import { pode } from '../../permissao'
import { ModalContato } from '../leads/ModalContato'
import { ModalDesfecho } from '../leads/ModalDesfecho'

/**
 * As etapas do funil, em quadradinhos inclinados, na linha do telefone do
 * alto da conversa (ideia e questionário de 06/10/2026).
 *
 * Cada quadradinho é uma etapa, na cor dela no quadro de Leads. A atual fica
 * acesa por inteiro e as outras pela metade, um pouco cinza e um pouco cor;
 * cliente fora do funil fica com todas num cinza apagado. Passar o mouse diz
 * o nome da etapa; clicar abre a negociação no meio da tela, a mesma janela
 * do quadro, e sem negociação o clique oferece criar uma.
 *
 * Substitui o texto "Funil / Etapa" que a linha mostrava: a etapa continua
 * escrita ao lado, e o desenho diz de relance o quanto a venda andou.
 */
export function EtapasDoFunil({
  conversa,
  usuario,
}: {
  conversa: ConversaDto
  usuario: UsuarioSessao
}) {
  const { data: funis } = useFunis()
  const { data: historico } = useHistoricoDoContato(conversa.contatoId)
  const { data: campos } = useCampos()
  const { data: tags } = useTags()
  const { data: usuarios } = useEquipe()
  const nova = useNovaNegociacao()
  const mover = useMoverEtapa()
  const { data: motivos } = useMotivosPerda()
  const [desfecho, setDesfecho] = useState<{ negociacao: NegociacaoDto; etapa: EtapaDto } | null>(
    null,
  )
  const { pedirConfirmacao, dialogo } = usePerguntar()
  const [abrindo, setAbrindo] = useState<NegociacaoDto | null>(null)

  const ativos = (funis ?? []).filter((f) => f.ativo)
  const funil =
    (conversa.funilId ? (funis ?? []).find((f) => f.id === conversa.funilId) : null) ?? ativos[0]
  if (!funil || funil.etapas.length === 0) return null

  const noFunil = Boolean(conversa.funilId === funil.id && conversa.etapaId)
  const abertas = (historico?.itens ?? []).filter((n) => n.status === 'aberta')
  const negociacao = abertas.find((n) => n.etapaId === conversa.etapaId) ?? abertas[0] ?? null
  const etapaAtual = funil.etapas.find((e) => e.id === conversa.etapaId)

  /*
    O clique move (mapa de uso, 08/10/2026): "os quadradinhos ao clicar movem
    a pessoa". O da etapa atual abre a negociação; os outros perguntam e
    movem, e Ganho e Perdido abrem a mesma janela do quadro de Leads, que
    pede o valor ou o motivo. Sem negociação, o clique oferece criar uma.
  */
  async function aoClicar(etapa: EtapaDto) {
    if (negociacao) {
      const podeMover = pode(usuario, 'leads.mover') || pode(usuario, 'leads.fechar')
      if (etapa.id === negociacao.etapaId || !podeMover) {
        setAbrindo(negociacao)
        return
      }
      if (etapa.tipo === 'ganho' || etapa.tipo === 'perda') {
        setDesfecho({ negociacao, etapa })
        return
      }
      const ok = await pedirConfirmacao(`Mover ${conversa.nome} para ${etapa.nome}?`, {
        titulo: 'Mudar etapa',
        rotuloConfirmar: 'Mover',
        perigo: false,
      })
      if (ok) mover.mutate({ id: negociacao.id, dados: { etapaId: etapa.id } })
      return
    }
    if (!pode(usuario, 'leads.editar') || !funil) return
    const ok = await pedirConfirmacao(
      `Abrir uma negociação para ${conversa.nome} no funil ${funil.nome}? Ela entra na primeira etapa.`,
      { titulo: 'Nova negociação', rotuloConfirmar: 'Abrir', perigo: false },
    )
    if (ok) nova.mutate({ contatoId: conversa.contatoId, funilId: funil.id })
  }

  return (
    <span
      className="flex min-w-0 items-center gap-1"
      // O nome do cliente em volta abre a ficha: o clique aqui é das etapas.
      onClick={(e) => e.stopPropagation()}
    >
      <QuadradinhosDoFunil
        funil={funil}
        etapaAtualId={noFunil ? (conversa.etapaId ?? null) : null}
        aoClicar={(etapa) => void aoClicar(etapa)}
        dica={(etapa, atual) => {
          const nome = `${funil.nome}: ${etapa.nome}${atual ? ' (etapa atual)' : ''}`
          if (!negociacao) return `${nome}. Clique para abrir uma negociação`
          return etapa.id === negociacao.etapaId
            ? `${nome}. Clique para abrir a negociação`
            : `${nome}. Clique para mover para esta etapa`
        }}
      />
      {noFunil && etapaAtual ? (
        <span className="truncate text-tinta-500">
          {funil.nome}: {etapaAtual.nome}
        </span>
      ) : null}

      {/*
        As janelas vão para o `body`. Montadas aqui dentro, herdavam a linha do
        telefone (letra miúda, sem quebrar linha, cortada com reticências), e a
        pergunta "Nova negociação" saía numa linha só, cortada, com barra de
        rolagem de lado. O clique dentro delas continua parando no
        `stopPropagation` acima, porque o React leva o evento pela árvore dele.
      */}
      {createPortal(
        <>
          <ModalContato
            aberto={abrindo !== null}
            contato={null}
            contatoId={abrindo?.contatoId ?? null}
            negociacao={abrindo}
            campos={campos ?? []}
            tags={tags ?? []}
            usuarios={usuarios ?? []}
            funis={ativos}
            funilPadrao={abrindo?.funilId ?? null}
            aoFechar={() => setAbrindo(null)}
          />
          <ModalDesfecho
            negociacao={desfecho?.negociacao ?? null}
            etapa={desfecho?.etapa ?? null}
            motivos={motivos ?? []}
            aoFechar={() => setDesfecho(null)}
          />
          {dialogo}
        </>,
        document.body,
      )}
    </span>
  )
}
